import { ICoords } from "../../@types/GoogleMaps/ICoord";

type Point = { lat: number; lng: number };

function toPoint(c: any): Point {
  if (typeof c.lat === "function" && typeof c.lng === "function") {
    return { lat: c.lat(), lng: c.lng() };
  }
  if (typeof c.lat === "number" && typeof c.lng === "number") {
    return { lat: c.lat, lng: c.lng };
  }
  // fallback for objects like {lat: { }, lng: { }}
  return { lat: Number(c.lat), lng: Number(c.lng) };
}

function pointInPolygon(point: Point, polygon: Point[]) {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].lng,
      yi = polygon[i].lat;
    const xj = polygon[j].lng,
      yj = polygon[j].lat;

    const intersect = yi > point.lat !== yj > point.lat &&
      point.lng < ((xj - xi) * (point.lat - yi)) / (yj - yi + (yj === yi ? 1e-12 : 0)) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

function polygonCentroid(points: Point[]) {
  let area = 0;
  let cx = 0;
  let cy = 0;
  const n = points.length;
  if (n === 0) return { lat: 0, lng: 0 };
  for (let i = 0; i < n; i++) {
    const p0 = points[i];
    const p1 = points[(i + 1) % n];
    const a = p0.lng * p1.lat - p1.lng * p0.lat;
    area += a;
    cx += (p0.lng + p1.lng) * a;
    cy += (p0.lat + p1.lat) * a;
  }
  area = area / 2;
  if (Math.abs(area) < 1e-12) {
    // degenerate polygon: fallback to average
    const avg = points.reduce((acc, p) => ({ lat: acc.lat + p.lat, lng: acc.lng + p.lng }), { lat: 0, lng: 0 });
    return { lat: avg.lat / n, lng: avg.lng / n };
  }
  cx = cx / (6 * area);
  cy = cy / (6 * area);
  return { lat: cy, lng: cx };
}

export function getCenterCoords(coords: ICoords) {
  const pts: Point[] = coords.map((c: any) => toPoint(c));

  // 1) try polygon centroid (area-weighted)
  const polyCent = polygonCentroid(pts);
  if (pointInPolygon(polyCent, pts)) return polyCent;

  // 2) try bounding box center
  let minLat = Infinity,
    maxLat = -Infinity,
    minLng = Infinity,
    maxLng = -Infinity;
  pts.forEach((p) => {
    if (p.lat < minLat) minLat = p.lat;
    if (p.lat > maxLat) maxLat = p.lat;
    if (p.lng < minLng) minLng = p.lng;
    if (p.lng > maxLng) maxLng = p.lng;
  });
  const bboxCenter = { lat: (minLat + maxLat) / 2, lng: (minLng + maxLng) / 2 };
  if (pointInPolygon(bboxCenter, pts)) return bboxCenter;

  // 3) try centroids of consecutive triangles
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % pts.length];
    const c = pts[(i + 2) % pts.length];
    const triCent = { lat: (a.lat + b.lat + c.lat) / 3, lng: (a.lng + b.lng + c.lng) / 3 };
    if (pointInPolygon(triCent, pts)) return triCent;
  }

  // 4) try edge midpoints nudged slightly toward average-of-vertices
  const avg = pts.reduce((acc, p) => ({ lat: acc.lat + p.lat, lng: acc.lng + p.lng }), { lat: 0, lng: 0 });
  const avgPt = { lat: avg.lat / pts.length, lng: avg.lng / pts.length };
  const nudges = [0.05, 0.1, 0.2, 0.4, 0.6];
  for (let i = 0; i < pts.length; i++) {
    const p0 = pts[i];
    const p1 = pts[(i + 1) % pts.length];
    const mid = { lat: (p0.lat + p1.lat) / 2, lng: (p0.lng + p1.lng) / 2 };
    const dx = avgPt.lng - mid.lng;
    const dy = avgPt.lat - mid.lat;
    for (const t of nudges) {
      const cand = { lat: mid.lat + dy * t, lng: mid.lng + dx * t };
      if (pointInPolygon(cand, pts)) return cand;
    }
  }

  // 5) as last resort return polygon centroid (even if outside) or first point
  return polyCent || pts[0] || bboxCenter;
}
